(() => {
  const SELECTORS = {
    drawer: '[data-mima-native-cart-drawer]',
    content: '[data-mima-cart-content]',
    cartBubble: '#cart-icon-bubble',
    recommendationsSubtext: '[data-mima-cart-recommendations-subtext]',
    recommendationsRoot: '[data-mima-cart-recommendations]',
    recommendationsTrack: '[data-mima-cart-recommendations-track]',
    recommendationsMore: '[data-mima-cart-recommendations-more]',
    recommendationsPrev: '[data-mima-cart-recommendations-prev]',
    recommendationsNext: '[data-mima-cart-recommendations-next]',
    recommendationsUrgency: '[data-mima-cart-recommendations-urgency]',
    recommendationsUrgencyText: '[data-mima-cart-recommendations-urgency-text]',
  };

  const REQUEST_TIMEOUT_MS = 12000;

  const DEFAULT_CONFIG = {
    packExtraEnabled: true,
    packExtraCode: 'PACK30-EXTRAS',
    packExtraPercent: 30,
    qualifyingPackCodes: ['JABON-2MAS1', 'JABON-3MAS2'],
    complementProductIds: [],
    soapProductId: null,
  };

  const lineLocks = new Set();
  let addLocked = false;
  let cartMutationLocked = false;
  let recommendationMutationInFlight = false;
  let refreshPromise = null;
  let refreshQueued = false;
  let openRequestPromise = null;
  let openRequested = false;
  let openTrigger = null;
  let cartRevision = 0;
  let recommendationsExpanded = false;
  let cartUrgencyListenerBound = false;

  const storefrontRoute = (path) =>
    `${window.Shopify?.routes?.root || '/'}${String(path || '').replace(/^\/+/, '')}`;
  const route = (name, fallback) => window.routes?.[name] || storefrontRoute(fallback);

  const normalizeDiscountCode = (code) => String(code || '').trim().toLocaleUpperCase();

  const readCartConfig = () => {
    const configNode = document.getElementById('MimaCartDrawerConfig');
    if (!configNode) return { ...DEFAULT_CONFIG };
    try {
      const parsed = JSON.parse(configNode.textContent || '{}');
      return {
        ...DEFAULT_CONFIG,
        ...parsed,
        qualifyingPackCodes: (parsed.qualifyingPackCodes || DEFAULT_CONFIG.qualifyingPackCodes).map(
          normalizeDiscountCode
        ),
        complementProductIds: (parsed.complementProductIds || [])
          .map((id) => Number(id))
          .filter(Boolean),
        soapProductId: Number(parsed.soapProductId) || null,
      };
    } catch (error) {
      return { ...DEFAULT_CONFIG };
    }
  };

  const fetchWithTimeout = async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      return await fetch(url, {
        ...options,
        signal: controller.signal,
      });
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('La solicitud tardó demasiado. Intenta nuevamente.');
      }
      throw error;
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const postCart = async (url, body, options = {}) => {
    const payload = { ...body };
    if (options.includeSections !== false) {
      payload.sections = ['cart-drawer', 'cart-icon-bubble'];
      payload.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || 'No se pudo actualizar el carrito.');
    }
    cartRevision += 1;
    return data;
  };

  const fetchCartJson = async () => {
    const response = await fetchWithTimeout(route('cart_url', '/cart') + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || 'No se pudo leer el carrito.');
    }
    return data;
  };

  const fetchRenderedSections = async (options = {}) => {
    const cartUrl = route('cart_url', '/cart');
    const separator = cartUrl.includes('?') ? '&' : '?';
    const cacheBust = options.cacheBust ? `&_=${Date.now()}` : '';
    const response = await fetchWithTimeout(
      `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble&sections_url=${encodeURIComponent(
        `${window.location.pathname}${window.location.search}`
      )}${cacheBust}`,
      {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      }
    );

    if (!response.ok) throw new Error('No se pudo actualizar el carrito.');
    return response.json();
  };

  const sectionNode = (markup, selector) => {
    if (!markup) return null;
    const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
    return documentFragment.querySelector(selector);
  };

  const renderCartBubble = (markup) => {
    const currentBubble = document.querySelector(SELECTORS.cartBubble);
    const nextBubble = sectionNode(markup, SELECTORS.cartBubble);
    if (currentBubble && nextBubble) {
      nextBubble.setAttribute('role', 'button');
      nextBubble.setAttribute('aria-haspopup', 'dialog');
      currentBubble.replaceWith(nextBubble);
      window.MimaFoam?.init?.(nextBubble);
    }
  };

  const progressRatio = (progressElement) => {
    if (!progressElement) return 0;
    const fill = progressElement.querySelector('.mima-native-cart__progress-fill');
    const trackWidth = progressElement.getBoundingClientRect().width;
    const fillWidth = fill?.getBoundingClientRect().width || 0;
    if (trackWidth > 0) return Math.max(0, Math.min(1, fillWidth / trackWidth));
    const configuredRatio = Number.parseFloat(
      progressElement.style.getPropertyValue('--mima-cart-progress-ratio')
    );
    return Number.isFinite(configuredRatio) ? Math.max(0, Math.min(1, configuredRatio)) : 0;
  };

  const renderDrawer = (markup) => {
    const drawer = document.querySelector(SELECTORS.drawer);
    const currentContent = drawer?.querySelector(SELECTORS.content);
    const nextContent = sectionNode(markup, SELECTORS.content);
    if (!drawer || !currentContent || !nextContent) return false;

    const previousProgress = progressRatio(
      currentContent.querySelector('.mima-native-cart__progress')
    );
    const nextProgress = nextContent.querySelector('.mima-native-cart__progress');
    const targetProgress = progressRatio(nextProgress);
    nextProgress?.style.setProperty('--mima-cart-progress-ratio', String(previousProgress));

    currentContent.replaceWith(nextContent);
    window.MimaFoam?.init?.(nextContent);
    drawer.classList.toggle('is-empty', !nextContent.querySelector('[data-mima-cart-items]'));
    if (nextProgress) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (nextProgress.isConnected) {
            nextProgress.style.setProperty('--mima-cart-progress-ratio', String(targetProgress));
          }
        });
      });
    }
    return true;
  };

  const renderSections = (sections) => {
    if (!sections) return false;
    const drawerRendered = renderDrawer(sections['cart-drawer']);
    renderCartBubble(sections['cart-icon-bubble']);
    if (drawerRendered) {
      enhanceCartRecommendationsUI();
    }
    return drawerRendered;
  };

  const attachSectionsToCart = async (cartData, preferredSections = null) => {
    if (cartData?.sections?.['cart-drawer']) return cartData;
    if (preferredSections?.['cart-drawer']) {
      return { ...cartData, sections: preferredSections };
    }
    const sections = await fetchRenderedSections({ cacheBust: true });
    return { ...cartData, sections };
  };

  const finalizeMimaCartMutation = async ({ cart, keepDrawerOpen = true } = {}) => {
    if (!cart) return false;

    let cartData = cart;
    if (!cartData.sections?.['cart-drawer']) {
      cartData = await attachSectionsToCart(cartData);
    }

    const syncedCart = await reconcileMimaCartPromotions(cartData);
    await finishMutation(syncedCart, {
      openDrawer: keepDrawerOpen,
      forceRefresh: !syncedCart?.sections?.['cart-drawer'],
    });
    clearRecommendationError();
    return true;
  };

  const buildComplementDiscountCodes = (cartData) => {
    const config = readCartConfig();
    if (!config.packExtraEnabled) {
      return discountTitlesFromCart(cartData);
    }

    const extraCode = getPackExtraCode();
    const codes = discountTitlesFromCart(cartData).filter((code) => !isDisabledDiscountCode(code));

    // Se conservan todos los códigos de pack del carrito, incluidos los que
    // ahora mismo están inaplicables. Agregar un complemento no debe reducir
    // el conjunto de promociones candidatas del jabón.
    packCandidatesForCart(cartData).forEach((code) => {
      if (!codes.includes(code)) codes.unshift(code);
    });
    if (cartHasSoap(cartData) && !codes.includes(extraCode)) {
      codes.push(extraCode);
    }

    return Array.from(new Set(codes));
  };

  const verifyComplementAdd = (beforeCart, finalCart, variantId) => {
    if (!verifyVariantAdded(beforeCart, finalCart, variantId)) return false;

    const config = readCartConfig();
    if (!config.packExtraEnabled) return true;

    if (!cartHasSoap(finalCart)) return true;

    return (
      discountTitlesFromCart(finalCart).includes(getPackExtraCode()) &&
      verifyPackExtraOnLine(finalCart, variantId)
    );
  };

  const refreshNativeCartDrawerFromServer = async (options = {}) => {
    const {
      force = false,
      open = false,
      bypassRecommendationGuard = false,
      cart: cartOverride = null,
      sections: sectionsOverride = null,
    } = options;

    if (!document.querySelector(SELECTORS.drawer)) return false;

    if (recommendationMutationInFlight && !bypassRecommendationGuard) {
      return false;
    }

    const revision = ++cartRevision;

    const resolveSections = async (preferredSections) => {
      if (preferredSections?.['cart-drawer']) return preferredSections;
      if (cartOverride?.sections?.['cart-drawer']) return cartOverride.sections;
      return fetchRenderedSections({ cacheBust: true });
    };

    const attemptRender = async (preferredSections) => {
      const sections = await resolveSections(preferredSections);
      if (!force && revision !== cartRevision) return false;
      return renderSections(sections);
    };

    let rendered = await attemptRender(sectionsOverride);

    if (!rendered) {
      rendered = await attemptRender(null);
    }

    const cartForEvent =
      cartOverride ||
      (await fetchCartJson().catch(() => null));

    if (cartForEvent) {
      emitCartMutation(cartForEvent, {
        openDrawer: open,
        suppressMutationEvent: bypassRecommendationGuard,
      });
    }

    if (open) openNativeDrawer();

    if (rendered) clearRecommendationError();

    return rendered;
  };

  window.refreshMimaCartDrawer = async (options = {}) => {
    if (!document.querySelector(SELECTORS.drawer)) return false;
    const force = Boolean(options.force);

    if (recommendationMutationInFlight) {
      if (refreshPromise) return refreshPromise;
      return false;
    }

    if (refreshPromise && !force) {
      refreshQueued = true;
      return refreshPromise;
    }

    if (refreshPromise && force) {
      refreshQueued = true;
      try {
        await refreshPromise;
      } catch (error) {
        // Ignore stale refresh failures; a forced refresh will run next.
      }
    }

    refreshPromise = (async () => {
      let rendered = false;
      do {
        refreshQueued = false;
        const revisionAtRequest = cartRevision;
        const sections = await fetchRenderedSections({ cacheBust: true });
        if (revisionAtRequest === cartRevision || force) {
          rendered = renderSections(sections);
        } else {
          refreshQueued = true;
        }
      } while (refreshQueued);
      return rendered;
    })()
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  };

  const isDisabledDiscountCode = (code) => normalizeDiscountCode(code).startsWith('NIVEL50');

  const discountTitlesFromCart = (cartData) => {
    const titles = [];
    (cartData?.discount_codes || []).forEach((entry) => {
      const code = normalizeDiscountCode(entry?.code);
      if (code && entry?.applicable !== false) titles.push(code);
    });
    (cartData?.cart_level_discount_applications || []).forEach((application) => {
      const title = normalizeDiscountCode(application?.title);
      if (title) titles.push(title);
    });
    (cartData?.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const title = normalizeDiscountCode(allocation?.discount_application?.title);
        if (title) titles.push(title);
      });
    });
    return Array.from(new Set(titles)).filter((code) => !isDisabledDiscountCode(code));
  };

  const getQualifyingPackCodes = () => {
    const config = readCartConfig();
    return (config.qualifyingPackCodes || []).map(normalizeDiscountCode).filter(Boolean);
  };

  const getPackExtraCode = () => normalizeDiscountCode(readCartConfig().packExtraCode);

  // El 30% en complementos es un cross-sell del jabon, no una rebaja general
  // del catalogo: sin jabon en el carrito no hay nada que complementar.
  //
  // La condicion vive aqui y no en el descuento porque Shopify no sabe
  // expresarla. "Tener al menos un jabon" solo se puede pedir con un BXGY, y
  // un BXGY ata el numero de complementos rebajados al numero de jabones -con
  // un jabon solo rebajaria uno de los tres- y ademas deja de combinarse con
  // JABON-MX-4. Ambas cosas medidas contra el carrito real.
  //
  // Esto cierra el caso normal: el tema deja de pedir el codigo cuando no hay
  // jabon. Un cliente que escriba PACK30-EXTRAS a mano todavia podria usarlo
  // sin jabon; cerrar tambien esa puerta exige un cambio en Shopify que hoy no
  // tiene solucion limpia.
  const cartHasSoap = (cartData) => {
    const soapProductId = readCartConfig().soapProductId;
    if (!soapProductId) return true;
    return (cartData?.items || []).some(
      (item) => Number(item.product_id) === Number(soapProductId)
    );
  };

  window.hasMimaQualifyingPackDiscount = (cartData) => {
    const config = readCartConfig();
    if (!config.packExtraEnabled) return false;
    const applied = discountTitlesFromCart(cartData);
    return getQualifyingPackCodes().some((code) => applied.includes(code));
  };

  const hasPackExtraAlreadyApplied = (cartData) => {
    const config = readCartConfig();
    if (!config.packExtraEnabled) return false;
    return discountTitlesFromCart(cartData).includes(getPackExtraCode());
  };

  // Devuelve TODOS los códigos de pack que Shopify tiene activos, no uno.
  //
  // Antes esto era getActivePackCode() y devolvía el primero que encontrara
  // recorriendo qualifyingPackCodes en orden. Con un solo código activo daba
  // igual, pero la auditoría demostró que Shopify puede activar dos a la vez
  // (8 unidades de la misma variante = JABON-2MAS1 + JABON-3MAS2 = 3 gratis),
  // y en ese caso "el primero del array" devolvía JABON-2MAS1: el de menor
  // ahorro, elegido por orden de declaración y no por el carrito.
  //
  // discountTitlesFromCart ya excluye los applicable:false, así que esto es
  // exactamente lo que Shopify considera vigente.
  const getActivePackCodes = (cartData) => {
    const applied = discountTitlesFromCart(cartData);
    return getQualifyingPackCodes().filter((code) => applied.includes(code));
  };

  // Códigos de pack que deben viajar como candidatos en la siguiente petición.
  //
  // Un código que Shopify marcó applicable:false no está "de más": es la vía
  // por la que vuelve a activarse solo cuando el carrito crece. Si se cayera de
  // la lista, un carrito que pasa de 5 a 8 unidades se quedaría con un solo
  // descuento y el cliente perdería la tercera unidad gratis.
  //
  // Solo se propagan si el carrito ya los traía: nunca se introducen en un
  // carrito que jamás tuvo jabón.
  const packCandidatesForCart = (cartData) => {
    const listed = new Set(
      (cartData?.discount_codes || []).map((entry) => normalizeDiscountCode(entry?.code))
    );
    const codes = getQualifyingPackCodes();
    return codes.some((code) => listed.has(code)) ? codes : [];
  };

  const getVariantQuantity = (cartData, variantId) => {
    const line = (cartData?.items || []).find((item) => Number(item.variant_id) === Number(variantId));
    return line ? Number(line.quantity) || 0 : 0;
  };

  const verifyVariantAdded = (beforeCart, afterCart, variantId) => {
    const beforeQty = getVariantQuantity(beforeCart, variantId);
    const afterQty = getVariantQuantity(afterCart, variantId);
    return afterQty === beforeQty + 1 && Number(afterCart?.item_count) === Number(beforeCart?.item_count) + 1;
  };

  const verifyPackExtraOnLine = (cartData, variantId) => {
    const config = readCartConfig();
    const item = (cartData?.items || []).find((line) => Number(line.variant_id) === Number(variantId));
    if (!item) return false;

    const extraCode = getPackExtraCode();
    const hasAllocation = (item.line_level_discount_allocations || []).some(
      (allocation) => normalizeDiscountCode(allocation?.discount_application?.title) === extraCode
    );
    const expectedUnitFinal = Math.round(
      item.original_price * (100 - Number(config.packExtraPercent || 30)) / 100
    );
    const actualUnitFinal =
      item.quantity > 0 ? Math.round(item.final_line_price / item.quantity) : item.final_line_price;
    const priceMatches = Math.abs(actualUnitFinal - expectedUnitFinal) <= 2;

    if (hasAllocation && priceMatches) return true;
    if (hasPackExtraAlreadyApplied(cartData) && item.final_line_price < item.original_line_price && priceMatches) {
      return true;
    }
    if (hasPackExtraAlreadyApplied(cartData) && item.final_line_price < item.original_line_price) {
      return true;
    }
    return false;
  };

  const reportRecommendationError = (message) => {
    const heading = document.querySelector(SELECTORS.recommendationsSubtext);
    if (!heading) return;
    heading.textContent = message;
    heading.setAttribute('role', 'alert');
  };

  const clearRecommendationError = () => {
    const heading = document.querySelector(SELECTORS.recommendationsSubtext);
    if (!heading) return;
    heading.removeAttribute('role');
    const offerDefault = heading.getAttribute('data-mima-cart-recommendations-offer-default');
    if (offerDefault) heading.textContent = offerDefault;
  };

  // Las recomendaciones se muestran ahora en un rail horizontal deslizable, así
  // que todas las cards ya son alcanzables y el colapso "Ver N más" sobra. Se
  // declaran todas visibles en vez de borrar syncRecommendationsCollapse():
  // así el propio helper oculta el botón y devuelve el foco a todas las cards,
  // sin tocar la fuente de productos ni el add-to-cart.
  const visibleRecommendationCount = () => Number.MAX_SAFE_INTEGER;

  const syncCartRecommendationsUrgency = (state = window.MimaAnnouncementUrgency) => {
    document.querySelectorAll(SELECTORS.recommendationsUrgency).forEach((urgencyEl) => {
      const textEl = urgencyEl.querySelector(SELECTORS.recommendationsUrgencyText);
      if (!state?.active || !state.displayText) {
        urgencyEl.hidden = true;
        if (textEl) textEl.textContent = '';
        return;
      }

      const displayText = String(state.displayText).trim().toLowerCase();
      if (textEl) textEl.textContent = displayText;
      urgencyEl.hidden = !displayText;
      if (state.accentColor) {
        urgencyEl.style.setProperty('--mima-announcement-urgency-color', state.accentColor);
      }
    });
  };

  const setRecommendationCardFocusable = (card, enabled) => {
    if (!card) return;
    card.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    card.querySelectorAll('a, button').forEach((el) => {
      if (enabled) {
        if (el.dataset.mimaSavedTabindex !== undefined) {
          if (el.dataset.mimaSavedTabindex === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', el.dataset.mimaSavedTabindex);
          delete el.dataset.mimaSavedTabindex;
        } else {
          el.removeAttribute('tabindex');
        }
      } else {
        if (el.dataset.mimaSavedTabindex === undefined) {
          el.dataset.mimaSavedTabindex = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : '';
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  };

  const syncRecommendationsCollapse = () => {
    const root = document.querySelector(SELECTORS.recommendationsRoot);
    const track = document.querySelector(SELECTORS.recommendationsTrack);
    const moreButton = document.querySelector(SELECTORS.recommendationsMore);
    if (!root || !track || !moreButton) return;

    const cards = Array.from(track.querySelectorAll('.mima-native-cart__recommendation'));
    const visibleCount = visibleRecommendationCount();
    const hiddenCount = Math.max(cards.length - visibleCount, 0);

    if (hiddenCount <= 0) {
      track.classList.remove('is-expanded');
      moreButton.hidden = true;
      moreButton.setAttribute('aria-expanded', 'false');
      cards.forEach((card) => setRecommendationCardFocusable(card, true));
      return;
    }

    moreButton.hidden = false;
    track.classList.toggle('is-expanded', recommendationsExpanded);
    moreButton.setAttribute('aria-expanded', recommendationsExpanded ? 'true' : 'false');
    moreButton.textContent = recommendationsExpanded ? 'Ver menos' : `Ver ${hiddenCount} más`;

    cards.forEach((card, index) => {
      const visible = recommendationsExpanded || index < visibleCount;
      setRecommendationCardFocusable(card, visible);
    });
  };

  // ---- Navegación del rail de recomendaciones (solo desktop, CSS oculta en móvil)
  const RECOMMENDATIONS_NAV_QUERY = '(min-width: 750px)';

  const recommendationsScrollStep = (track) => {
    const card = track.querySelector('.mima-native-cart__recommendation');
    if (!card) return Math.round(track.clientWidth * 0.8);
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return Math.round(card.getBoundingClientRect().width + gap);
  };

  const syncRecommendationsNav = () => {
    const track = document.querySelector(SELECTORS.recommendationsTrack);
    const prev = document.querySelector(SELECTORS.recommendationsPrev);
    const next = document.querySelector(SELECTORS.recommendationsNext);
    if (!track || !prev || !next) return;

    // Sin desbordamiento no hay nada que navegar: se ocultan por completo.
    // El listener se engancha siempre, antes de cualquier retorno anticipado:
    // si se ata solo cuando el rail es navegable, un primer render sin
    // desbordamiento deja los botones sin actualizar para siempre.
    if (track.dataset.mimaNavBound !== 'true') {
      track.dataset.mimaNavBound = 'true';
      track.addEventListener('scroll', () => {
        window.requestAnimationFrame(syncRecommendationsNav);
      });
    }

    const overflow = track.scrollWidth - track.clientWidth;
    const navigable = overflow > 2 && window.matchMedia(RECOMMENDATIONS_NAV_QUERY).matches;
    prev.hidden = !navigable;
    next.hidden = !navigable;
    if (!navigable) return;

    const left = track.scrollLeft;
    prev.disabled = left <= 2;
    next.disabled = left >= overflow - 2;
  };

  // Se asigna scrollLeft en vez de usar scrollBy({behavior:'smooth'}): con este
  // rail la animación programática nunca llegaba a arrancar y el desplazamiento
  // quedaba en 0 (medido: con 'auto' avanzaba, con 'smooth' no). La suavidad la
  // aporta `scroll-behavior` en el CSS, que además respeta prefers-reduced-motion.
  const scrollRecommendations = (direction) => {
    const track = document.querySelector(SELECTORS.recommendationsTrack);
    if (!track) return;
    const step = recommendationsScrollStep(track) * direction;
    const max = track.scrollWidth - track.clientWidth;
    track.scrollLeft = Math.max(0, Math.min(max, track.scrollLeft + step));
    // No se depende del evento scroll para refrescar los extremos.
    syncRecommendationsNav();
  };

  const enhanceCartRecommendationsUI = () => {
    syncCartRecommendationsUrgency(window.MimaAnnouncementUrgency);
    syncRecommendationsCollapse();
    syncRecommendationsNav();
  };

  const ensureCartUrgencyListener = () => {
    if (cartUrgencyListenerBound) return;
    cartUrgencyListenerBound = true;
    document.addEventListener('mima:announcement-urgency-change', (event) => {
      syncCartRecommendationsUrgency(event.detail || window.MimaAnnouncementUrgency);
    });
    window.addEventListener('resize', () => {
      syncRecommendationsCollapse();
    });
  };

  const reportLineError = (lineElement, message) => {
    const errorElement = lineElement?.querySelector('[data-mima-cart-line-error]');
    if (!errorElement) return;
    errorElement.textContent = message;
    errorElement.hidden = !message;
  };

  const emitCartMutation = (cartData, options = {}) => {
    document.dispatchEvent(
      new CustomEvent('mima:cart-updated', {
        detail: { source: 'mima-native-cart', cart: cartData },
      })
    );
    if (options.suppressMutationEvent) return;
    window.dispatchEvent(
      new CustomEvent('mima:cart-mutated', {
        detail: {
          source: 'mima-native-cart',
          openDrawer: Boolean(options.openDrawer),
          cart: cartData,
        },
      })
    );
  };

  const finishMutation = async (cartData, options = {}) => {
    const rendered = await refreshNativeCartDrawerFromServer({
      cart: cartData,
      sections: cartData?.sections,
      open: Boolean(options.openDrawer),
      force: Boolean(options.forceRefresh),
      bypassRecommendationGuard: true,
    });

    if (!rendered) {
      await window.refreshMimaCartDrawer?.({ force: true }).catch(() => {});
      emitCartMutation(cartData, options);
      if (options.openDrawer) openNativeDrawer();
    }
  };

  const readVariantIdFromButton = (button) => {
    const raw = button.dataset.variantId || button.dataset.mimaCartAdd;
    const variantId = Number(raw);
    if (!Number.isInteger(variantId) || variantId <= 0) return null;
    return variantId;
  };

  // Suma, por código, lo que Shopify asignó realmente en el carrito. Un código
  // puede figurar como applicable:true y aun así no descontar nada (p. ej. no se
  // alcanzó la cantidad mínima), así que `applicable` por sí solo no demuestra
  // que la promoción se haya aplicado.
  const discountAllocationsByCode = (cartData) => {
    const totals = {};
    const add = (title, amount) => {
      const code = normalizeDiscountCode(title);
      if (!code) return;
      totals[code] = (totals[code] || 0) + (Number(amount) || 0);
    };
    (cartData?.cart_level_discount_applications || []).forEach((application) => {
      add(application?.title, application?.total_allocated_amount);
    });
    (cartData?.items || []).forEach((item) => {
      (item?.line_level_discount_allocations || []).forEach((allocation) => {
        add(allocation?.discount_application?.title, allocation?.amount);
      });
    });
    return totals;
  };

  // Se expone para que las secciones de la PDP usen exactamente esta política
  // en vez de construir cada una su propio conjunto de códigos.
  window.reconcileMimaPackDiscounts = async (cartData) => {
    const source = cartData || (await fetchCartJson());
    return reconcilePackDiscounts(source);
  };

  window.applyMimaCartDiscountCodes = async (codes, options = {}) => {
    const codesToApply = (Array.isArray(codes) ? codes : [codes])
      .map(normalizeDiscountCode)
      .filter(Boolean)
      .filter((code) => !isDisabledDiscountCode(code));

    if (!codesToApply.length && !(options.removeCodes || []).length) {
      return { ok: false, reason: 'empty', message: 'No hay códigos para aplicar.' };
    }
    if (cartMutationLocked && !options.bypassMutationLock) {
      return { ok: false, reason: 'busy', message: 'El carrito se está actualizando.' };
    }

    const shouldLock = !options.bypassMutationLock;
    if (shouldLock) cartMutationLocked = true;
    try {
      /* De aqui solo se necesita saber que codigos trae ya el carrito, y eso
         viene en cualquier respuesta Ajax de Shopify. Cuando el llamador acaba
         de recibir una -/cart.js, /cart/update.js o /cart/change.js- releerla
         era un round trip de mas en la primera mutacion y en cada alta de
         complemento.

         La frescura la garantiza el llamador, no esta funcion: solo se le pasa
         `cart` desde rutas donde el objeto es la respuesta inmediata de la
         peticion anterior. Aqui unicamente se comprueba que tenga la forma de
         un carrito de Shopify; cualquier otra cosa cae al GET de siempre, que
         es tambien lo que ocurre con los llamadores que no pasan nada. */
      const suministrado = options.cart;
      const sirve =
        Boolean(suministrado) &&
        Array.isArray(suministrado.items) &&
        Array.isArray(suministrado.discount_codes);
      const freshCart = sirve ? suministrado : await fetchCartJson();
      let requested = discountTitlesFromCart(freshCart);

      const replaceSet = new Set((options.replaceCodes || []).map(normalizeDiscountCode));
      if (replaceSet.size) requested = requested.filter((code) => !replaceSet.has(code));

      const removeSet = new Set((options.removeCodes || []).map(normalizeDiscountCode));
      if (removeSet.size) requested = requested.filter((code) => !removeSet.has(code));

      (options.preserveCodes || []).forEach((code) => {
        const normalized = normalizeDiscountCode(code);
        if (normalized && !requested.includes(normalized)) requested.push(normalized);
      });

      codesToApply.forEach((code) => {
        if (!requested.includes(code)) requested.push(code);
      });

      requested = Array.from(new Set(requested.filter((code) => !isDisabledDiscountCode(code))));

      const cartData = await postCart(route('cart_update_url', '/cart/update.js'), {
        discount: requested.join(','),
      });

      if (!options.skipFinishMutation) {
        await finishMutation(cartData, { openDrawer: Boolean(options.openDrawer) });
      }

      const acceptedTitles = discountTitlesFromCart(cartData);
      const conflictingQuickCodes = acceptedTitles.filter(
        (title) => replaceSet.has(title) && !codesToApply.includes(title)
      );

      // `allocationGroup` declara un conjunto de códigos que compiten entre sí
      // — los BXGY del jabón — y del que Shopify elige cuáles activa según el
      // contenido real del carrito.
      //
      // Para esos códigos el éxito NO puede ser "todos quedaron activos".
      // Mandar JABON-2MAS1 y JABON-3MAS2 con 5 unidades hace que Shopify
      // desactive el primero a propósito, porque el segundo ahorra más: exigir
      // que ambos sobrevivieran marcaría como fallo justamente el mejor
      // resultado posible. El criterio pasa a ser "al menos uno del grupo
      // quedó activo"; los códigos fuera del grupo siguen siendo obligatorios.
      //
      // Los packs del jabón compiten SIEMPRE, así que son grupo por defecto en
      // cualquier llamada y no solo en las que se acuerdan de declararlo. Sin
      // esto, agregar un complemento reventaba: buildComplementDiscountCodes
      // manda los dos candidatos junto a PACK30-EXTRAS, Shopify desactiva el
      // pack que no califica — el caso normal — y `every` daba el conjunto por
      // rechazado. El complemento se había agregado bien y con su 30 %, pero
      // addRecommendation leía ok:false y lo revertía.
      const groupSet = new Set(
        (options.allocationGroup || getQualifyingPackCodes()).map(normalizeDiscountCode)
      );
      const groupCodes = codesToApply.filter((code) => groupSet.has(code));
      const soloCodes = codesToApply.filter((code) => !groupSet.has(code));

      let accepted = true;
      if (codesToApply.length) {
        accepted = soloCodes.every((code) => acceptedTitles.includes(code));
        if (groupCodes.length) {
          accepted = accepted && groupCodes.some((code) => acceptedTitles.includes(code));
        }
      }
      if (removeSet.size) {
        accepted =
          accepted &&
          !Array.from(removeSet).some((code) => acceptedTitles.includes(normalizeDiscountCode(code)));
      }
      accepted = accepted && conflictingQuickCodes.length === 0;

      // Verificación económica: se exige asignación real, no solo applicable.
      // Solo altera `ok` cuando el llamador lo pide con requireAllocation, para
      // no cambiar el comportamiento de los flujos que ya dependen de esta API.
      //
      // Para el grupo competidor la comprobación es agregada: importa que el
      // grupo haya descontado dinero de verdad, no cuál de sus códigos lo hizo.
      // Quien pulsa X3 y acaba con X5 activo porque su carrito da para más ha
      // recibido un resultado correcto, y antes esto lo registraba como error.
      const allocations = discountAllocationsByCode(cartData);
      const unallocatedSolo = soloCodes.filter((code) => !(allocations[code] > 0));
      const groupAllocated = groupCodes.reduce((sum, code) => sum + (allocations[code] || 0), 0);
      const unallocated = unallocatedSolo.concat(
        groupCodes.length && groupAllocated <= 0 ? groupCodes : []
      );
      let reason = accepted
        ? 'applied'
        : conflictingQuickCodes.length
          ? 'conflicting_discount'
          : 'not_applicable';
      if (options.requireAllocation && accepted && unallocated.length) {
        accepted = false;
        reason = 'no_allocation';
      }

      return {
        ok: accepted,
        reason,
        message: accepted
          ? codesToApply.length === 1
            ? `Código ${codesToApply[0]} aplicado.`
            : 'Descuentos actualizados.'
          : reason === 'no_allocation'
            ? 'El código se aceptó pero no descontó nada.'
            : conflictingQuickCodes.length
              ? 'El código anterior sigue activo.'
              : 'Código no válido',
        cart: cartData,
        appliedCodes: acceptedTitles,
        allocations,
        unallocatedCodes: unallocated,
      };
    } catch (error) {
      return {
        ok: false,
        reason: 'request_failed',
        message: error.message || 'No se pudo aplicar el código.',
        error,
      };
    } finally {
      if (shouldLock) cartMutationLocked = false;
    }
  };

  window.applyMimaCartDiscountCode = async (code, options = {}) =>
    window.applyMimaCartDiscountCodes([code], options);

  // ---------------------------------------------------------------------
  // Resolver único de las promociones del jabón.
  //
  // Hasta ahora los BXGY solo se intentaban cuando el cliente pulsaba una
  // tarjeta de oferta, porque el código viajaba pegado a ese clic. Quien subía
  // la cantidad con el "+" y pulsaba Agregar al carrito se llevaba 5 jabones a
  // precio completo: el tema nunca le ofrecía los códigos a Shopify. Las
  // tarjetas tienen que ser atajos de selección, no la única puerta al
  // descuento.
  //
  // La política es una sola y vive aquí: si el carrito contiene el jabón, sus
  // códigos de pack deben estar presentes como candidatos. Nada más. No se
  // decide qué promoción gana, no se calculan unidades gratis y no se toca
  // ningún precio — eso lo resuelve Shopify y se lee de vuelta de cart.js.
  //
  // Tampoco hace falta un umbral de cantidad. Está medido: con los códigos ya
  // en el carrito, Shopify reevalúa la aplicabilidad solo en cada cambio de
  // cantidad — de 2 a 3 unidades activa JABON-2MAS1 sin que nadie reenvíe
  // nada, y por debajo del mínimo los deja inaplicables sin descontar. Por eso
  // esto solo actúa cuando falta algún candidato, y las mutaciones normales no
  // cuestan una petición extra.
  const reconcilePackDiscounts = async (cartData) => {
    if (recommendationMutationInFlight) return cartData;

    const packCodes = getQualifyingPackCodes();
    if (!packCodes.length) return cartData;

    const soapProductId = readCartConfig().soapProductId;
    if (!soapProductId) return cartData;

    const hasSoap = (cartData?.items || []).some(
      (item) => Number(item.product_id) === Number(soapProductId)
    );
    if (!hasSoap) return cartData;

    const listed = new Set(
      (cartData?.discount_codes || []).map((entry) => normalizeDiscountCode(entry?.code))
    );
    if (packCodes.every((code) => listed.has(code))) return cartData;

    const result = await window.applyMimaCartDiscountCodes(packCodes, {
      // `cartData` es la respuesta de la mutacion que acaba de ocurrir.
      cart: cartData,
      allocationGroup: packCodes,
      skipFinishMutation: true,
      bypassMutationLock: true,
      suppressMutationEvent: true,
    });
    return result.cart || cartData;
  };

  // Punto único por el que pasa toda mutación del carrito, venga del drawer o
  // de cualquier sección de la PDP. Primero los packs del jabón, después el
  // extra de complementos, que depende de si hay pack activo.
  const reconcileMimaCartPromotions = async (cartData) => {
    const withPacks = await reconcilePackDiscounts(cartData);
    return syncPackExtraDiscountState(withPacks);
  };

  const syncPackExtraDiscountState = async (cartData) => {
    if (recommendationMutationInFlight) return cartData;

    // Limpieza de códigos de pack huérfanos.
    //
    // Política deliberada: un JABON-* inaplicable NO se borra mientras haya
    // algo en el carrito, porque es justo el mecanismo por el que la promoción
    // vuelve a activarse sola cuando el cliente sube de 5 a 8 unidades.
    // Borrarlo ahí le costaría al cliente una unidad gratis.
    //
    // Con el carrito vacío ya no puede reactivarse con nada, así que se retira
    // para que no sobreviva a la sesión ni llegue al checkout. PACK30-EXTRAS
    // no se toca aquí: su ciclo lo gobiernan los complementos.
    if (!(cartData?.items || []).length) {
      const packCodes = getQualifyingPackCodes();
      const stale = (cartData?.discount_codes || [])
        .map((entry) => normalizeDiscountCode(entry?.code))
        .filter((code) => packCodes.includes(code));
      if (stale.length) {
        const cleaned = await window.applyMimaCartDiscountCodes([], {
          cart: cartData,
          removeCodes: stale,
          skipFinishMutation: true,
          bypassMutationLock: true,
          suppressMutationEvent: true,
        });
        return cleaned.cart || cartData;
      }
    }

    const config = readCartConfig();
    if (!config.packExtraEnabled) return cartData;

    const applied = discountTitlesFromCart(cartData);
    const extraCode = getPackExtraCode();
    const hasExtra = applied.includes(extraCode);
    const hasPack = window.hasMimaQualifyingPackDiscount(cartData);
    const hasComplements = (cartData?.items || []).some((item) =>
      (readCartConfig().complementProductIds || []).includes(Number(item.product_id))
    );

    const hasSoap = cartHasSoap(cartData);

    if (hasPack && hasComplements && hasSoap && !hasExtra) {
      const codes = [...packCandidatesForCart(cartData), extraCode];
      const result = await window.applyMimaCartDiscountCodes(codes, {
        cart: cartData,
        skipFinishMutation: true,
        bypassMutationLock: true,
        suppressMutationEvent: true,
      });
      return result.cart || cartData;
    }

    if (!hasPack && hasComplements && hasSoap && !hasExtra) {
      const result = await window.applyMimaCartDiscountCodes([extraCode], {
        cart: cartData,
        skipFinishMutation: true,
        bypassMutationLock: true,
        suppressMutationEvent: true,
      });
      return result.cart || cartData;
    }

    // Aquí vivía la política que reducía varios JABON-* a uno solo. Se elimina:
    // dos códigos de pack activos a la vez es un estado legítimo que Shopify
    // produce cuando el carrito da para las dos promociones, y colapsarlos le
    // quitaba al cliente una unidad gratis. Si ambos están activos y el extra
    // también, no hay nada que reconciliar.
    return cartData;
  };

  const rollbackRecommendationAdd = async (beforeCart, variantId, options = {}) => {
    const afterCart = await fetchCartJson();
    const beforeLine = (beforeCart.items || []).find((item) => Number(item.variant_id) === Number(variantId));
    const afterLine = (afterCart.items || []).find((item) => Number(item.variant_id) === Number(variantId));

    if (!afterLine) return attachSectionsToCart(afterCart);

    const nextQuantity = beforeLine ? beforeLine.quantity : 0;
    const rolledBack = await postCart(
      route('cart_change_url', '/cart/change.js'),
      { id: afterLine.key, quantity: nextQuantity },
      { includeSections: false }
    );

    // Se restauran todos los códigos de pack que tenía el carrito antes del
    // intento fallido, no solo el primero: revertir un complemento no puede
    // costarle al cliente una promoción que ya tenía.
    const codesToRestore = [...packCandidatesForCart(beforeCart)];
    const hadPack = codesToRestore.length > 0;
    if (hasPackExtraAlreadyApplied(beforeCart) && !options.removePackExtraOnRestore) {
      codesToRestore.push(getPackExtraCode());
    }

    let restoredCart = rolledBack;
    if (codesToRestore.length) {
      const restored = await window.applyMimaCartDiscountCodes(codesToRestore, {
        skipFinishMutation: true,
        bypassMutationLock: true,
        suppressMutationEvent: true,
      });
      restoredCart = restored.cart || rolledBack;
    } else if (hadPack && options.removePackExtraOnRestore) {
      const restored = await window.applyMimaCartDiscountCodes(packCandidatesForCart(beforeCart), {
        removeCodes: [getPackExtraCode()],
        skipFinishMutation: true,
        bypassMutationLock: true,
        suppressMutationEvent: true,
      });
      restoredCart = restored.cart || rolledBack;
    }

    return attachSectionsToCart(restoredCart.items ? restoredCart : await fetchCartJson());
  };

  /* Espera larga: el aviso llega tarde a proposito.

     La fila ya se atenua y deja de aceptar clics en cuanto se pulsa, que es la
     respuesta inmediata. Lo que faltaba es senal de que la cosa sigue viva
     cuando la espera se alarga: en datos moviles una mutacion puede tardar de
     1,5 a 3 s, y ahi una fila gris quieta parece rota.

     Pero mostrar el spinner desde el primer milisegundo lo haria parpadear en
     las operaciones normales, que rondan los 800 ms en wifi. Por eso aparece
     solo si se superan los 600 ms, que es el umbral a partir del cual la espera
     se percibe como tal.

     No toca cantidades ni precios: eso sigue llegando del servidor.

     Devuelve la funcion de limpieza para llamarla en el `finally`, de modo que
     un error tampoco pueda dejar un spinner huerfano ni la fila bloqueada. */
  const UMBRAL_ESPERA_MS = 600;

  const marcarOcupado = (element) => {
    if (!element) return () => {};
    element.setAttribute('aria-busy', 'true');
    const temporizador = setTimeout(() => {
      element.classList.add('is-pending');
    }, UMBRAL_ESPERA_MS);
    return () => {
      clearTimeout(temporizador);
      element.classList.remove('is-pending');
    };
  };

  const changeLine = async (key, quantity, lineElement) => {
    if (!key || lineLocks.has(key) || cartMutationLocked) return;
    lineLocks.add(key);
    cartMutationLocked = true;
    const liberar = marcarOcupado(lineElement);
    reportLineError(lineElement, '');

    try {
      const cartData = await postCart(route('cart_change_url', '/cart/change.js'), {
        id: key,
        quantity: Math.max(0, quantity),
      });
      await finalizeMimaCartMutation({ cart: cartData, keepDrawerOpen: true });
    } catch (error) {
      lineElement?.setAttribute('aria-busy', 'false');
      reportLineError(lineElement, error.message);
    } finally {
      liberar();
      lineLocks.delete(key);
      cartMutationLocked = false;
    }
  };

  // Cambia la cantidad de una variante que Shopify tiene repartida en varias
  // líneas.
  //
  // Con un BXGY activo, "5 Aclarante" son dos line items: la parte pagada y la
  // gratis. El cliente ve una sola fila con 5 y espera que el "+" le deje 6.
  // Tocar una línea suelta no sirve: subir la pagada de 3 a 4 deja 6 unidades
  // pero descuadra el reparto. Se calcula el total nuevo y se distribuye entre
  // las líneas del grupo en una sola petición; después Shopify vuelve a repartir
  // el descuento como le corresponda y el resumen se recalcula solo.
  const changeVariantGroup = async (keysAttribute, currentQuantity, delta, groupElement) => {
    const keys = String(keysAttribute || '').split(',').map((k) => k.trim()).filter(Boolean);
    if (!keys.length || cartMutationLocked) return;
    if (keys.some((key) => lineLocks.has(key))) return;

    const target = Math.max(0, Number(currentQuantity) + Number(delta));

    keys.forEach((key) => lineLocks.add(key));
    cartMutationLocked = true;
    const liberar = marcarOcupado(groupElement);
    reportLineError(groupElement, '');

    try {
      const fresh = await fetchCartJson();
      const updates = {};
      let left = target;
      keys.forEach((key) => {
        const line = (fresh.items || []).find((item) => item.key === key);
        const available = line ? Number(line.quantity) || 0 : 0;
        const take = Math.min(left, available);
        updates[key] = take;
        left -= take;
      });
      // Si el cliente sube por encima de lo que el grupo ya tiene, el resto se
      // añade a la primera línea.
      if (left > 0) updates[keys[0]] = (updates[keys[0]] || 0) + left;

      const cartData = await postCart(route('cart_update_url', '/cart/update.js'), { updates });
      await finalizeMimaCartMutation({ cart: cartData, keepDrawerOpen: true });
    } catch (error) {
      groupElement?.setAttribute('aria-busy', 'false');
      reportLineError(groupElement, error.message);
    } finally {
      liberar();
      keys.forEach((key) => lineLocks.delete(key));
      cartMutationLocked = false;
    }
  };

  // Quita de una sola vez las dos líneas reales que forman un pack BXGY.
  //
  // Borrar solo la pagada dejaría en el carrito la línea gratis huérfana, y
  // Shopify le devolvería su precio completo al caer el descuento: el cliente
  // pediría quitar el pack y acabaría con unidades sueltas cobradas. Un único
  // /cart/update.js con todas las claves a 0 las elimina en la misma
  // transacción. No toca códigos ni allocations: solo cantidades.
  const removePack = async (keysAttribute, packElement) => {
    const keys = String(keysAttribute || '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean);
    if (!keys.length || cartMutationLocked) return;
    if (keys.some((key) => lineLocks.has(key))) return;

    keys.forEach((key) => lineLocks.add(key));
    cartMutationLocked = true;
    const liberar = marcarOcupado(packElement);
    reportLineError(packElement, '');

    try {
      const updates = {};
      keys.forEach((key) => {
        updates[key] = 0;
      });
      const cartData = await postCart(route('cart_update_url', '/cart/update.js'), { updates });
      await finalizeMimaCartMutation({ cart: cartData, keepDrawerOpen: true });
    } catch (error) {
      packElement?.setAttribute('aria-busy', 'false');
      reportLineError(packElement, error.message);
    } finally {
      liberar();
      keys.forEach((key) => lineLocks.delete(key));
      cartMutationLocked = false;
    }
  };

  // Cambia el único carácter del CTA circular. Nunca se escriben palabras:
  // el texto descriptivo vive solo en aria-label.
  const setAddGlyph = (button, glyph) => {
    const span = button.querySelector('span');
    if (span) span.textContent = glyph;
  };

  const addRecommendation = async (button) => {
    if (!button || button.disabled || button.dataset.loading === 'true' || addLocked) return;

    const variantId = readVariantIdFromButton(button);
    const originalAriaLabel = button.getAttribute('aria-label');

    if (!variantId) {
      reportRecommendationError('No se pudo identificar la variante del complemento.');
      return;
    }

    let finalCart = null;
    let shouldFinalize = false;
    let errorMessage = '';

    addLocked = true;
    recommendationMutationInFlight = true;
    button.disabled = true;
    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');
    // El botón es un círculo de 44px: escribir texto dentro lo desbordaba y
    // tapaba la card. El feedback ahora es puramente visual (spinner / ✓ vía
    // data-mima-add-state) y el texto vive solo en aria-label.
    button.dataset.mimaAddState = 'loading';
    setAddGlyph(button, '');
    button.setAttribute(
      'aria-label',
      button.dataset.labelLoading || 'Agregando producto al carrito'
    );
    clearRecommendationError();

    const config = readCartConfig();

    try {
      const beforeCart = await fetchCartJson();

      await postCart(
        route('cart_add_url', '/cart/add.js'),
        { items: [{ id: variantId, quantity: 1 }] },
        { includeSections: false }
      );

      const afterAddCart = await fetchCartJson();
      if (!verifyVariantAdded(beforeCart, afterAddCart, variantId)) {
        throw new Error('No se pudo agregar el complemento al carrito.');
      }

      if (config.packExtraEnabled) {
        const discountCodes = buildComplementDiscountCodes(afterAddCart);
        let discountResult = await window.applyMimaCartDiscountCodes(discountCodes, {
          // `afterAddCart` acaba de salir de /cart.js, dos lineas mas arriba.
          cart: afterAddCart,
          skipFinishMutation: true,
          bypassMutationLock: true,
          suppressMutationEvent: true,
        });

        let verifiedCart = await fetchCartJson();
        if (discountResult.cart?.sections) {
          verifiedCart = { ...verifiedCart, sections: discountResult.cart.sections };
        }

        if (!discountResult.ok || !verifyComplementAdd(beforeCart, verifiedCart, variantId)) {
          discountResult = await window.applyMimaCartDiscountCodes(discountCodes, {
            skipFinishMutation: true,
            bypassMutationLock: true,
            suppressMutationEvent: true,
          });
          verifiedCart = await fetchCartJson();
          if (discountResult.cart?.sections) {
            verifiedCart = { ...verifiedCart, sections: discountResult.cart.sections };
          }
        }

        if (!discountResult.ok || !verifyComplementAdd(beforeCart, verifiedCart, variantId)) {
          finalCart = await rollbackRecommendationAdd(beforeCart, variantId, {
            removePackExtraOnRestore: true,
          });
          errorMessage = 'No pudimos agregar el producto con el descuento. Intenta nuevamente.';
          shouldFinalize = true;
        } else {
          finalCart = verifiedCart;
          shouldFinalize = true;
        }
      } else {
        finalCart = afterAddCart;
        shouldFinalize = true;
      }
    } catch (error) {
      errorMessage = error.message || 'No se pudo agregar el complemento.';
      try {
        const freshCart = await fetchCartJson();
        if (getVariantQuantity(freshCart, variantId) >= 1) {
          finalCart = freshCart;
          shouldFinalize = true;
          errorMessage = '';
        }
      } catch (refreshError) {
        // Commerce state unknown; keep errorMessage.
      }
    } finally {
      recommendationMutationInFlight = false;
      addLocked = false;
      if (button.isConnected) {
        button.disabled = false;
        delete button.dataset.loading;
        button.removeAttribute('aria-busy');
        const restoreIdle = () => {
          if (!button.isConnected) return;
          delete button.dataset.mimaAddState;
          setAddGlyph(button, '+');
          const idle = button.dataset.labelIdle || originalAriaLabel;
          if (idle) button.setAttribute('aria-label', idle);
        };
        if (shouldFinalize && finalCart) {
          // Confirmación breve: el ✓ suele desaparecer con el re-render que
          // saca la recomendación del rail, pero si sigue ahí vuelve al "+".
          button.dataset.mimaAddState = 'done';
          setAddGlyph(button, '✓');
          button.setAttribute('aria-label', button.dataset.labelDone || 'Agregado al carrito');
          window.setTimeout(restoreIdle, 1800);
        } else {
          restoreIdle();
        }
      }
    }

    if (shouldFinalize && finalCart) {
      await finalizeMimaCartMutation({ cart: finalCart, keepDrawerOpen: true });
    } else if (errorMessage) {
      reportRecommendationError(errorMessage);
    }
  };

  const openNativeDrawer = (trigger) => {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;
    if (drawer.classList.contains('active')) return;
    recommendationsExpanded = false;
    if (typeof drawer.open === 'function') {
      drawer.open(trigger);
      enhanceCartRecommendationsUI();
      return;
    }
    drawer.classList.add('active');
    document.body.classList.add('overflow-hidden');
    enhanceCartRecommendationsUI();
  };

  const requestDrawerOpen = async (trigger) => {
    openRequested = true;
    if (trigger) openTrigger = trigger;
    if (openRequestPromise) return openRequestPromise;

    openRequestPromise = (async () => {
      try {
        await window.refreshMimaCartDrawer?.();
      } catch (error) {
        console.warn('[Mi Má cart] No se pudo refrescar antes de abrir.', error);
      }
      if (openRequested) openNativeDrawer(openTrigger);
      openRequested = false;
      openTrigger = null;
    })().finally(() => {
      openRequestPromise = null;
    });

    return openRequestPromise;
  };

  const installNativeDrawerAdapter = () => {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer || drawer.dataset.mimaAdapterInstalled === 'true') return;
    drawer.dataset.mimaAdapterInstalled = 'true';
    drawer.renderContents = async (parsedState) => {
      if (recommendationMutationInFlight && !parsedState?.__bypassRecommendationGuard) return;
      cartRevision += 1;
      let cartData = parsedState;
      if (!cartData?.items) {
        try {
          const freshCart = await fetchCartJson();
          cartData = { ...freshCart, sections: parsedState?.sections || freshCart.sections };
        } catch (error) {
          cartData = parsedState;
        }
      }
      const syncedCart = await syncPackExtraDiscountState(cartData);
      const rendered = renderSections(syncedCart?.sections || parsedState?.sections);
      if (!rendered) await window.refreshMimaCartDrawer?.({ force: true });
      window.setTimeout(() => openNativeDrawer(), 0);
    };
  };

  installNativeDrawerAdapter();
  ensureCartUrgencyListener();
  enhanceCartRecommendationsUI();
  document.addEventListener('DOMContentLoaded', () => {
    installNativeDrawerAdapter();
    ensureCartUrgencyListener();
    enhanceCartRecommendationsUI();
  }, { once: true });

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.target.closest(SELECTORS.cartBubble) || !document.querySelector(SELECTORS.drawer)) return;
      event.stopImmediatePropagation();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const cartTrigger = event.target.closest(SELECTORS.cartBubble);
      if (!cartTrigger || !document.querySelector(SELECTORS.drawer)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      requestDrawerOpen(cartTrigger);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      const cartTrigger = event.target.closest(SELECTORS.cartBubble);
      if (!cartTrigger || !document.querySelector(SELECTORS.drawer) || event.code !== 'Space') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      requestDrawerOpen(cartTrigger);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-mima-cart-close]');
    if (closeButton) {
      recommendationsExpanded = false;
      const drawer = closeButton.closest(SELECTORS.drawer);
      if (typeof drawer?.close === 'function') drawer.close();
      return;
    }

    const moreButton = event.target.closest(SELECTORS.recommendationsMore);
    if (moreButton) {
      event.preventDefault();
      recommendationsExpanded = !recommendationsExpanded;
      syncRecommendationsCollapse();
      return;
    }

    const quantityButton = event.target.closest('[data-mima-cart-quantity]');
    if (quantityButton) {
      const lineElement = quantityButton.closest('[data-mima-cart-line]');
      const currentQuantity = Number(
        lineElement?.querySelector('[data-mima-cart-quantity-value]')?.textContent
      );
      const adjustment = Number(quantityButton.dataset.mimaCartQuantity);
      const groupKeys = lineElement?.dataset.mimaCartGroupKeys;
      if (groupKeys) {
        changeVariantGroup(groupKeys, currentQuantity, adjustment, lineElement);
      } else {
        changeLine(quantityButton.dataset.lineKey, currentQuantity + adjustment, lineElement);
      }
      return;
    }

    const addPackButton = event.target.closest('[data-mima-cart-add-pack]');
    if (addPackButton) {
      if (addPackButton.disabled) return;
      const packElement = addPackButton.closest('[data-mima-cart-pack]');
      const groupQty = Number(addPackButton.dataset.mimaPackGroupQty);
      const packUnits = Number(addPackButton.dataset.mimaPackUnits);
      // Un pack entero, no una unidad: subir el grupo en `packUnits` es el
      // único paso que deja todas las unidades dentro de una asignación.
      // Shopify reparte el descuento y el drawer se repinta desde cart.js, así
      // que si el tope ya estaba agotado se verá como línea normal en vez de
      // como pack — nunca como un precio inventado.
      if (groupQty && packUnits) {
        changeVariantGroup(addPackButton.dataset.mimaCartAddPack, groupQty, packUnits, packElement);
      }
      return;
    }

    const removePackButton = event.target.closest('[data-mima-cart-remove-pack]');
    if (removePackButton) {
      const packElement = removePackButton.closest('[data-mima-cart-pack]');
      const groupQty = Number(removePackButton.dataset.mimaPackGroupQty);
      const packUnits = Number(removePackButton.dataset.mimaPackUnits);
      // Una variante puede estar pintada como varias tarjetas: quitar una
      // retira solo sus unidades y deja el resto. Si la tarjeta representa al
      // grupo entero, se elimina como antes.
      if (groupQty && packUnits && packUnits < groupQty) {
        changeVariantGroup(removePackButton.dataset.mimaCartRemovePack, groupQty, -packUnits, packElement);
      } else {
        removePack(removePackButton.dataset.mimaCartRemovePack, packElement);
      }
      return;
    }

    const removeButton = event.target.closest('[data-mima-cart-remove]');
    if (removeButton) {
      const lineElement = removeButton.closest('[data-mima-cart-line]');
      // La papelera pertenece a la variante que se ve, y esa variante puede
      // estar repartida en varias líneas: se quitan todas las suyas y ninguna
      // ajena. Con esto el botón hace exactamente lo que la fila dice.
      const target = removeButton.dataset.mimaCartRemove || '';
      if (target.includes(',')) {
        removePack(target, lineElement);
      } else {
        changeLine(target, 0, lineElement);
      }
      return;
    }

    const prevButton = event.target.closest(SELECTORS.recommendationsPrev);
    if (prevButton) {
      scrollRecommendations(-1);
      return;
    }

    const nextButton = event.target.closest(SELECTORS.recommendationsNext);
    if (nextButton) {
      scrollRecommendations(1);
      return;
    }

    const addButton = event.target.closest('[data-mima-cart-add]');
    if (addButton) addRecommendation(addButton);
  });

  window.addEventListener('mima:cart-mutated', async (event) => {
    if (event.detail?.source === 'mima-native-cart' || recommendationMutationInFlight) return;
    cartRevision += 1;
    let cartData = event.detail?.cart;
    if (cartData) {
      // Se resuelven las promociones ANTES de pintar. Si el drawer se abriera
      // con el carrito tal y como salió de cart/add.js, el cliente vería el
      // precio sin descuento y medio segundo después otro distinto.
      cartData = await reconcileMimaCartPromotions(cartData);
    }
    const renderedFromEvent = await refreshNativeCartDrawerFromServer({
      cart: cartData,
      sections: cartData?.sections,
      open: Boolean(event.detail?.openDrawer),
      force: true,
    });
    if (event.detail?.openDrawer && !renderedFromEvent) {
      requestDrawerOpen();
    }
  });

  window.addEventListener('cart:open', (event) => {
    if (event.detail?.source === 'mima-native-cart') return;
    if (['mima-product-form', 'mima-quick-offer'].includes(event.detail?.source)) return;
    cartRevision += 1;
    requestDrawerOpen();
  });
})();
